'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';
import { StoreItemSheet } from './store-item-sheet';
import type { MenuItem } from '@/types/item';

interface StoreItemSheetContextType {
  openSheet: (
    item: MenuItem | null,
    storeContext?: {
      pageId?: string;
      slug?: string;
      branchId?: string;
    }
  ) => void;
  closeSheet: () => void;
  isOpen: boolean;
  currentItem: MenuItem | null;
}

const StoreItemSheetContext = createContext<
  StoreItemSheetContextType | undefined
>(undefined);

export function StoreItemSheetProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<MenuItem | null>(null);
  const [currentStoreContext, setCurrentStoreContext] = useState<
    | {
        pageId?: string;
        slug?: string;
        branchId?: string;
      }
    | undefined
  >(undefined);

  const openSheet = useCallback(
    (
      item: MenuItem | null,
      storeContext?: {
        pageId?: string;
        slug?: string;
        branchId?: string;
      }
    ) => {
      setCurrentItem(item);
      setCurrentStoreContext(storeContext);
      setIsOpen(!!item);
    },
    []
  );

  const closeSheet = useCallback(() => {
    setIsOpen(false);

    // Dispatch a custom event when sheet is closed
    const event = new Event('sheet-closed');
    document.dispatchEvent(event);

    // We'll keep the current item during animation, then clear it
    setTimeout(() => {
      setCurrentItem(null);
      setCurrentStoreContext(undefined);
    }, 300); // Adjust timing to match your animation duration
  }, []);

  return (
    <StoreItemSheetContext.Provider
      value={{
        openSheet,
        closeSheet,
        isOpen,
        currentItem,
      }}
    >
      {children}
      <StoreItemSheet
        item={currentItem}
        onClose={closeSheet}
        storeContext={currentStoreContext}
      />
    </StoreItemSheetContext.Provider>
  );
}

export function useStoreItemSheet() {
  const context = useContext(StoreItemSheetContext);
  if (context === undefined) {
    throw new Error(
      'useStoreItemSheet must be used within a StoreItemSheetProvider'
    );
  }
  return context;
}
